import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { text } from '../app/i18n';
import { formatNumber, type Answers, type Entry, type Inputs, type Section } from './form-targets';
import { duplicateKey } from './form-warnings';
import { civilToPadText } from './pad-input';
import { draftMissing, draftWarnings, duplicatedValues, focusFieldOf, ledgerEntries, ledgerLeave, ledgerPanelEntries, ledgerPosition, nextDraft, rowNonconform } from './series-ledger';
import { evaluateFormCondition } from '@ayumia/component-runtime';
import { TemplateEntry } from './TemplateEntry';
import './series-ledger.css';
import { panelControl, panelVisible } from './ledger-panel';

/**
 * Série présentée en registre (inspection-template-body 1.6.0, `series.presentation: ledger` ;
 * addendum d'ADR-141 du 2026-10-19) : un tableau dense des lignes — on ajoute en fin de liste, la
 * dernière est mise en évidence, toucher une ligne la rouvre en correction — et un panneau de saisie
 * fixe : tuiles d'un choix, champs au pavé (la touche Entrée passe au suivant, puis ajoute), autres
 * champs. Après l'ajout, le curseur revient sur `ledger.focus_field` et les champs `keep_fields`
 * restent choisis. Les avertissements (doublon, motif manquant) se voient sur la ligne, jamais
 * bloquants. Aucun terme métier : tout vient du gabarit. Même parcours que la tablette.
 */

export type LedgerRow = Answers;
type SeriesSectionType = Section & { series: NonNullable<Section['series']> };

/** Un champ saisi au pavé : nombre, ou chaîne déclarée `keypad`. */
export function padKeyable(entry: Entry): boolean {
  const type = entry.field.type;
  if (type === 'number') return true;
  return type === 'string' && (entry as { presentation?: { widget?: string } }).presentation?.widget === 'keypad';
}

function enumOptions(entry: Entry): Array<{ value: string; label: string }> {
  const validation = entry.field.validation as { enum_values?: string[]; enum_labels?: Record<string, Readonly<Record<string, string>>> } | undefined;
  return (validation?.enum_values ?? []).map((value) => ({ value, label: text(validation?.enum_labels?.[value], 'fr', value) }));
}

/** Valeur d'une cellule du tableau. */
export function cellText(entry: Entry, value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  if (entry.field.type === 'date') return civilToPadText(value);
  if (entry.field.type === 'enumeration') return enumOptions(entry).find((option) => option.value === value)?.label ?? String(value);
  if (typeof value === 'number') return `${formatNumber(value)}${(entry.field as { unit?: string }).unit === undefined ? '' : ` ${(entry.field as { unit?: string }).unit}`}`;
  return String(value);
}

/** Texte tapé au pavé → réponse : un nombre (virgule décimale) ou une chaîne de chiffres. */
function padValue(entry: Entry, typed: string): unknown {
  if (typed === '') return null;
  if (entry.field.type === 'number') {
    const number = Number(typed.replace(',', '.'));
    return typed.endsWith(',') || Number.isNaN(number) ? typed : number;
  }
  return typed;
}

function padShown(value: unknown): string {
  if (typeof value === 'number') return String(value).replace('.', ',');
  return typeof value === 'string' ? value : '';
}

/** Touche du pavé sur le texte du champ : chiffre, virgule (nombre), effacer un chiffre, tout effacer. */
export function pressKey(entry: Entry, typed: string, key: string): string {
  const max = (entry.field.validation as { max_length?: number } | undefined)?.max_length;
  if (key === 'C') return '';
  if (key === '⌫') return typed.slice(0, -1);
  if (key === ',') return entry.field.type === 'number' && !typed.includes(',') ? (typed === '' ? '0,' : `${typed},`) : typed;
  if (!/^\d$/.test(key)) return typed;
  if (max !== undefined && typed.length >= max) return typed;
  return `${typed}${key}`;
}

/** Ligne prête à partir : les réponses en cours de frappe (« 18, ») deviennent des nombres. */
function settled(entries: readonly Entry[], draft: LedgerRow): LedgerRow {
  const out: LedgerRow = {};
  for (const [key, value] of Object.entries(draft)) {
    const entry = entries.find((candidate) => candidate.field.field_id === key);
    if (value === null || value === undefined || value === '') continue;
    out[key] = entry !== undefined && typeof value === 'string' && (entry.field.type === 'number') ? Number(value.replace(',', '.')) : value;
  }
  return out;
}

/** Les champs au pavé qui se suivent vont côte à côte (bague et poids), les autres un par ligne. */
function groupPads(entries: readonly Entry[], render: (entry: Entry) => ReactNode): ReactNode[] {
  const out: ReactNode[] = [];
  let group: ReactNode[] = [];
  const flush = (): void => {
    if (group.length > 0) out.push(<div key={`pads-${out.length}`} className="ay-ledger__pads">{group}</div>);
    group = [];
  };
  for (const entry of entries) {
    if (padKeyable(entry)) group.push(render(entry));
    else {
      flush();
      out.push(render(entry));
    }
  }
  flush();
  return out;
}

export function SeriesLedger({
  section,
  rows,
  onChange,
  disabled,
  errors,
  inputs,
  warnings = {},
  form = {},
  open,
}: {
  section: SeriesSectionType;
  rows: LedgerRow[];
  onChange: (next: LedgerRow[]) => void;
  disabled: boolean;
  errors: Readonly<Record<string, string>>;
  inputs: Inputs;
  warnings?: Readonly<Record<string, readonly string[]>>;
  /** Réponses de la fiche (`$form` des avertissements de la ligne). */
  form?: Answers;
  /** Ligne à rouvrir en correction (une alerte de la synthèse) ; `nonce` la redemande. */
  open?: { index: number; nonce: number } | undefined;
}) {
  const sid = section.section_id;
  const entries = ledgerEntries(section);
  const panelEntries = ledgerPanelEntries(section);
  const keyable = entries.filter(padKeyable);
  const focusField = focusFieldOf(section, padKeyable);
  const max = (section.series as { max_count?: number }).max_count;
  const [draft, setDraft] = useState<LedgerRow>(() => (nextDraft(section, rows.at(-1) ?? {})));
  const [editing, setEditing] = useState<number | null>(null);
  const [last, setLast] = useState<number>(rows.length - 1);
  const [field, setField] = useState<string | undefined>(focusField);
  /** Champ texte ouvert en saisie (un clic explicite) ; replié sinon, comme à la tablette. */
  const [textOpen, setTextOpen] = useState<string | null>(null);
  /** Choix obligatoire encore vide que le bouton vient de désigner (ses tuiles se signalent). */
  const [attention, setAttention] = useState<string | null>(null);
  const inputsRef = useRef<Record<string, HTMLInputElement | null>>({});
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const dup = new Map(entries.filter((entry) => (entry as { duplicates?: { in_series?: boolean } }).duplicates?.in_series === true).map((entry) => [entry.field.field_id, duplicatedValues(rows, entry.field.field_id)]));

  // Le curseur suit le champ en cours après chaque rendu demandé (ajout, Suivant, correction).
  const [focusTick, setFocusTick] = useState(0);
  const focus = (id: string | undefined): void => {
    setField(id);
    setFocusTick((tick) => tick + 1);
  };
  useEffect(() => {
    if (focusTick > 0 && field !== undefined) inputsRef.current[field]?.focus();
  }, [focusTick]);
  // Un champ texte ouvert d'un clic prend le focus ; il se replie à l'ajout, à la correction et au pavé.
  useEffect(() => {
    if (textOpen !== null) document.getElementById(`input-ledger-${sid}-${textOpen}`)?.focus();
  }, [textOpen]);
  const reopen = (index: number): void => {
    setTextOpen(null);
    setEditing(index);
    setDraft({ ...rows[index] });
    focus(focusField);
  };
  useEffect(() => {
    if (open !== undefined && rows[open.index] !== undefined) reopen(open.index);
  }, [open?.nonce]);
  useEffect(() => {
    if (editing === null && bodyRef.current !== null) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [rows.length, editing]);

  const set = (id: string, value: unknown): void => setDraft((current) => ({ ...current, [id]: value }));
  /** Champs obligatoires vides, le champ en cours d'abord (on reste sur le poids tant qu'il manque). */
  const missingNow = (): string[] => {
    const missing = draftMissing(section, settled(section.fields, draft));
    return field !== undefined && missing.includes(field) ? [field, ...missing.filter((id) => id !== field)] : missing;
  };
  const commit = (): void => {
    const row = settled(section.fields, draft);
    // Une ligne incomplète ne part jamais : le bouton mène d'abord au champ obligatoire qui manque.
    if (Object.keys(row).length === 0 || draftMissing(section, row).length > 0) return;
    if (editing !== null) {
      onChange(rows.map((current, index) => (index === editing ? row : current)));
      setLast(editing);
      setEditing(null);
    } else {
      if (max !== undefined && rows.length >= max) return;
      onChange([...rows, row]);
      setLast(rows.length);
    }
    setDraft(nextDraft(section, row));
    setTextOpen(null);
    setAttention(null);
    focus(focusField);
  };
  /**
   * D'une ligne à l'autre (« ‹ », « › », comme « Carcasse suivante » de l'écran v1, demande du 2026-10-19) :
   * une ligne changée complète est gardée avant de partir, une ligne changée incomplète retient (le champ
   * qui manque est montré). `target` : la ligne à rouvrir, ou `null` pour une nouvelle ligne. Comme la tablette.
   */
  const go = (target: number | null): void => {
    const row = settled(section.fields, draft);
    const leave = ledgerLeave(section, rows, editing, row);
    if (leave === 'stay') {
      enter();
      return;
    }
    let next: Answers[] = rows;
    if (leave === 'save') {
      next = editing !== null ? rows.map((current, index) => (index === editing ? row : current)) : [...rows, row];
      onChange(next);
    }
    setTextOpen(null);
    setAttention(null);
    if (target === null) {
      setEditing(null);
      setLast(next.length - 1);
      setDraft(nextDraft(section, next.at(-1) ?? {}));
    } else {
      setEditing(target);
      setDraft({ ...next[target] });
    }
    focus(focusField);
  };
  /** Supprimer la ligne en correction ; la saisie repart sur une nouvelle ligne. */
  const remove = (): void => {
    if (editing === null) return;
    const next = rows.filter((_, index) => index !== editing);
    onChange(next);
    setEditing(null);
    setLast(-1);
    setTextOpen(null);
    setAttention(null);
    setDraft(nextDraft(section, next.at(-1) ?? {}));
    focus(focusField);
  };
  const enter = (): void => {
    const position = keyable.findIndex((entry) => entry.field.field_id === field);
    if (position >= 0 && position + 1 < keyable.length) return focus(keyable[position + 1]!.field.field_id);
    const missing = missingNow();
    if (missing.length === 0) return commit();
    // Un champ obligatoire manque : le curseur y va (pavé), ses tuiles se signalent (choix), ou le texte s'ouvre.
    const entry = panelEntries.find((candidate) => candidate.field.field_id === missing[0]);
    if (entry === undefined) return;
    if (padKeyable(entry)) {
      setAttention(null);
      focus(entry.field.field_id);
    } else if (entry.field.type === 'enumeration') setAttention(entry.field.field_id);
    else setTextOpen(entry.field.field_id);
  };
  const current = keyable.find((entry) => entry.field.field_id === field);
  const press = (key: string): void => {
    setTextOpen(null);
    if (current === undefined) return;
    const id = current.field.field_id;
    set(id, padValue(current, pressKey(current, padShown(draft[id]), key)));
  };
  const pending = draftWarnings(section, settled(section.fields, draft), rows, editing, form);
  const pendingOf = (id: string): string[] => pending.filter((warning) => warning.field === id).map((warning) => warning.message);
  const decimal = current !== undefined && current.field.type === 'number';
  const keys = ['7', '8', '9', '4', '5', '6', '1', '2', '3', decimal ? ',' : 'C', '0', '⌫'];
  const lastKey = keyable.at(-1)?.field.field_id;
  // « Suivant » tant qu'un champ au pavé suit ou qu'un champ obligatoire manque ; « Ajouter » seulement complet.
  const missing = missingNow();
  const missingEntry = panelEntries.find((entry) => entry.field.field_id === missing[0]);
  const enterLabel = field !== undefined && field !== lastKey && keyable.some((entry) => entry.field.field_id === field)
    ? `Suivant › ${text(keyable[keyable.findIndex((entry) => entry.field.field_id === field) + 1]?.field.label, 'fr', '')}`
    : missingEntry !== undefined ? `Suivant › ${text(missingEntry.field.label, 'fr', missingEntry.field.field_id)}`
    : editing !== null ? 'Enregistrer la ligne' : 'Ajouter';
  // Colonnes lisibles (ADR-146 addendum (15)) : une largeur minimale par colonne, l'en-tête passe à la ligne au
  // lieu de chevaucher sa voisine ; sous 420 px de large, le tableau devient une pile de cartes (CSS).
  const columns = `2.75em ${entries.map((entry) => (entry.field.type === 'enumeration' ? 'minmax(5em, 1fr)' : padKeyable(entry) ? 'minmax(4.5em, 6em)' : 'minmax(5.5em, 1.4fr)')).join(' ')}`;
  const gridStyle = { '--ay-ledger-columns': columns } as CSSProperties;

  return (
    <div className="ay-ledger-host">
    <div className="ay-ledger" aria-label={text(section.label, 'fr', sid)}>
      <section className="ay-ledger__table" aria-label="Lignes saisies">
        <div className="ay-ledger__head" style={gridStyle} role="row">
          <span>N°</span>
          {entries.map((entry) => (
            <span key={entry.field.field_id} className={padKeyable(entry) && entry.field.type !== 'string' ? 'ay-ledger__num' : undefined} title={text(entry.field.label, 'fr', entry.field.field_id)}>{text(entry.field.label, 'fr', entry.field.field_id)}</span>
          ))}
        </div>
        <div className="ay-ledger__body" ref={bodyRef}>
          {rows.length === 0 && <p className="ay-ledger__empty">Aucune ligne : la première s’ajoute au panneau.</p>}
          {rows.map((row, index) => {
            const nonconform = rowNonconform(section, row);
            const state = index === editing ? 'editing' : index === last && editing === null ? 'last' : undefined;
            const rowError = Object.keys(errors).some((path) => path.startsWith(`answers.${sid}.${index}.`));
            return (
              <button
                key={index}
                type="button"
                className={`ay-ledger__row${state === undefined ? '' : ` ay-ledger__row--${state}`}${rowError ? ' ay-ledger__row--error' : ''}`}
                style={gridStyle}
                data-testid={`ledger-row-${index}`}
                aria-label={`Ligne ${index + 1}`}
                aria-current={state === 'editing' ? 'true' : undefined}
                disabled={disabled}
                onClick={() => reopen(index)}
              >
                <span className="ay-ledger__num ay-ledger__muted">{String(index + 1).padStart(2, '0')}</span>
                {entries.map((entry) => {
                  const id = entry.field.field_id;
                  const shown = cellText(entry, row[id]);
                  const cellWarnings = warnings[`answers.${sid}.${index}.${id}`] ?? [];
                  const duplicated = dup.get(id)?.has(duplicateKey(row[id]) ?? '\u0000') === true;
                  const tone = entry.nonconformity_when !== undefined ? (nonconform ? 'danger' : 'success') : duplicated || (shown === '' && cellWarnings.length > 0) ? 'warning' : undefined;
                  return (
                    <span key={id} className={`ay-ledger__cell${tone === undefined ? '' : ` ay-ledger__cell--${tone}`}${padKeyable(entry) && entry.field.type !== 'string' ? ' ay-ledger__num' : ''}`} title={cellWarnings.join(' · ') || shown || undefined} data-label={text(entry.field.label, 'fr', id)}>
                      {shown === '' && cellWarnings.length > 0 ? cellWarnings[0] : shown}
                    </span>
                  );
                })}
              </button>
            );
          })}
        </div>
        {errors[`answers.${sid}`] !== undefined && <span className="ay-field__error">{errors[`answers.${sid}`]}</span>}
      </section>

      <aside className="ay-ledger__panel" aria-label="Saisie de la ligne">
        <div className="ay-ledger__panel-title">
          <span className="ay-ledger__eyebrow" data-testid={`ledger-title-${sid}`}>{`Ligne ${ledgerPosition(rows.length, editing).current} / ${ledgerPosition(rows.length, editing).total}`}</span>
          {editing !== null && (
            <button type="button" className="ay-button ay-button--ghost ay-ledger__delete" disabled={disabled} onClick={remove}>
              Supprimer
            </button>
          )}
          {editing !== null && (
            <button type="button" className="ay-button ay-button--ghost" onClick={() => { setEditing(null); setTextOpen(null); setDraft(nextDraft(section, rows.at(-1) ?? {})); focus(focusField); }}>
              Annuler
            </button>
          )}
        </div>
        {/* ADR-147 : un champ de ligne caché par visible_when (motif d'une ligne conforme) n'est pas proposé. */}
        {groupPads(panelEntries.filter((entry) => panelVisible(entry, draft)), (entry): ReactNode => {
          const id = entry.field.field_id;
          const label = text(entry.field.label, 'fr', id);
          const error = editing === null ? undefined : errors[`answers.${sid}.${editing}.${id}`];
          // ADR-147 §1 : une longue énumération (le motif de non-conformité, 32 valeurs) se choisit dans une liste, pas en tuiles.
          if (panelControl(entry) === 'list') {
            return (
              <label key={id} className={`ay-ledger__list${attention === id ? ' ay-ledger__tiles--attention' : ''}`}>
                <span className="ay-ledger__eyebrow">{label}</span>
                <select data-testid={`ledger-list-${id}`} aria-label={label} disabled={disabled} value={typeof draft[id] === 'string' ? (draft[id] as string) : ''} onChange={(event) => {
                  set(id, event.target.value === '' ? null : event.target.value);
                  if (attention === id) setAttention(null);
                }}>
                  <option value="">—</option>
                  {enumOptions(entry).map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
                {error !== undefined && <span className="ay-field__error">{error}</span>}
              </label>
            );
          }
          if (entry.field.type === 'enumeration') {
            return (
              <div key={id} className={`ay-ledger__tiles${attention === id ? ' ay-ledger__tiles--attention' : ''}`} role="group" aria-label={label}>
                {enumOptions(entry).map((option) => {
                  const on = draft[id] === option.value;
                  const nc = on && entry.nonconformity_when !== undefined && rowNonconform(section, { ...draft, [id]: option.value });
                  return (
                    <button key={option.value} type="button" aria-pressed={on} disabled={disabled} className={`ay-ledger__tile${on ? (nc ? ' ay-ledger__tile--danger' : ' ay-ledger__tile--on') : ''}`} onMouseDown={(event) => event.preventDefault()} onClick={() => {
                      // Retoucher la tuile choisie d'un choix obligatoire la laisse choisie : on change en touchant une autre.
                      const required = entry.field.required === true || (entry.required_when !== undefined && evaluateFormCondition(entry.required_when, draft));
                      set(id, on && !required ? null : option.value);
                      if (attention === id) setAttention(null);
                    }}>
                      {option.label}
                    </button>
                  );
                })}
                {error !== undefined && <span className="ay-field__error">{error}</span>}
              </div>
            );
          }
          if (padKeyable(entry)) {
            const on = field === id;
            return (
              <label key={id} className={`ay-ledger__pad${on ? ' ay-ledger__pad--on' : ''}`}>
                <span className="ay-ledger__eyebrow">{label}{(entry.field as { unit?: string }).unit === undefined ? '' : ` · ${(entry.field as { unit?: string }).unit}`}</span>
                <input
                  ref={(element) => { inputsRef.current[id] = element; }}
                  type="text"
                  inputMode={entry.field.type === 'number' ? 'decimal' : 'numeric'}
                  autoComplete="off"
                  aria-label={label}
                  data-testid={`ledger-input-${id}`}
                  disabled={disabled}
                  value={padShown(draft[id])}
                  placeholder="—"
                  onFocus={() => {
                    setField(id);
                    setTextOpen(null);
                  }}
                  onChange={(event) => {
                    const typed = [...event.target.value].reduce((acc, char) => pressKey(entry, acc, char === '.' ? ',' : char), '');
                    set(id, padValue(entry, typed));
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      enter();
                    }
                  }}
                />
                {/* Avertissement (doublon) ou erreur dans la case, sur une place réservée : rien ne bouge. */}
                <span className={`ay-ledger__pad-note${error !== undefined ? ' ay-ledger__pad-note--error' : ''}`} role="status" data-testid={`ledger-note-${id}`}>
                  {error ?? pendingOf(id)[0] ?? ''}
                </span>
              </label>
            );
          }
          if (textOpen !== id) {
            // Replié : la valeur, ou le libellé, ou son avertissement (motif manquant) ; un clic l'ouvre.
            const value = typeof draft[id] === 'string' ? (draft[id] as string) : '';
            const warning = pendingOf(id)[0];
            return (
              <button
                key={id}
                type="button"
                className={`ay-ledger__line${warning !== undefined ? ' ay-ledger__line--warn' : ''}${error !== undefined ? ' ay-ledger__line--error' : ''}`}
                aria-label={label}
                data-testid={`ledger-line-${id}`}
                disabled={disabled}
                onClick={() => setTextOpen(id)}
              >
                <span className={value === '' ? 'ay-ledger__muted' : undefined}>{value !== '' ? value : (warning ?? label)}</span>
              </button>
            );
          }
          return (
            <div key={id} className="ay-ledger__other">
              <TemplateEntry entry={entry} values={draft} inputs={inputs} controlId={`ledger-${sid}-${id}`} required={false} disabled={disabled} onChange={(value) => set(id, value)} onFill={(filled) => setDraft((current) => ({ ...current, ...filled }))} warnings={pendingOf(id)} {...(error === undefined ? {} : { error })} />
            </div>
          );
        })}
        {keyable.length > 0 && (
          <div className="ay-ledger__keys" aria-label="Pavé numérique">
            {keys.map((key) => (
              <button key={key} type="button" disabled={disabled || current === undefined} aria-label={key === 'C' ? 'Effacer' : key === '⌫' ? 'Effacer un chiffre' : key === ',' ? 'Virgule' : key} onMouseDown={(event) => event.preventDefault()} onClick={() => press(key)}>
                {key}
              </button>
            ))}
          </div>
        )}
        <div className="ay-ledger__enter-row">
          <button type="button" className="ay-button ay-button--secondary ay-ledger__arrow" aria-label="Ligne précédente" disabled={disabled || (editing ?? rows.length) === 0} onMouseDown={(event) => event.preventDefault()} onClick={() => go((editing ?? rows.length) - 1)}>
            ‹
          </button>
          <button type="button" className="ay-button ay-ledger__enter" data-testid={`ledger-enter-${sid}`} disabled={disabled || (editing === null && max !== undefined && rows.length >= max)} onClick={enter}>
            {enterLabel}
          </button>
          {editing !== null && (
            <button type="button" className="ay-button ay-button--secondary ay-ledger__arrow" aria-label={editing + 1 < rows.length ? 'Ligne suivante' : 'Ligne suivante (nouvelle)'} disabled={disabled} onMouseDown={(event) => event.preventDefault()} onClick={() => go(editing + 1 < rows.length ? editing + 1 : null)}>
              {editing + 1 < rows.length ? '›' : '+'}
            </button>
          )}
        </div>
      </aside>
    </div>
    </div>
  );
}
